/**
 * CR-6348: Reworked SO Lines filter panel for the Supplier View.
 *
 * Groups filters into labelled sections (Date Range / Company / Supplier /
 * Order Details) per the design, re-introduces the legacy ODD Status and OTD
 * Status to LSPD filters, and preserves the existing dependent-filter cascades
 * (Division -> Site, Company Country -> Site -> Warehouse, Supplier Group ->
 * Supplier Site). Existing input types and CR-4716 permissions are unchanged.
 *
 * Rendered only in supplier-portal mode; the customer PO Lines view keeps its
 * existing flat grid (see POLinesFiltersPanel).
 */
import { Card, CardContent, CardHeader, CardTitle } from '../../../../ui/card';
import { Button } from '../../../../ui/button';
import { Search, X, Calendar, Building2, Truck, FileText } from 'lucide-react';
import { POLinesFilters, FilterOptions } from '../types';
import { useAuth } from '../../../../../contexts/AuthContext';
import { FIELD_CLS, LABEL_CLS, FilterSection } from './fieldStyles';
import { CompanySection } from './sections/CompanySection';
import { SupplierSection } from './sections/SupplierSection';
import { OrderDetailsSection } from './sections/OrderDetailsSection';
import { usePOLinesFilterCascades } from './usePOLinesFilterCascades';

/** Horizontal divider with padding so it doesn't touch card edges */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

interface SupplierSOLinesFiltersProps {
  filters: POLinesFilters;
  filterOptions: FilterOptions | null;
  onFilterChange: (key: string, value: string) => void;
  onSearch: () => void;
  onClearFilters: () => void;
  onHideFilters?: () => void;
}

export function SupplierSOLinesFilters({
  filters,
  filterOptions,
  onFilterChange,
  onSearch,
  onClearFilters,
  onHideFilters,
}: SupplierSOLinesFiltersProps) {
  const { user } = useAuth();
  const isSupplierUser = user?.platform === 'SUPPLIER';

  const divisions = filterOptions?.divisions || [];
  const supplierGroups = filterOptions?.supplier_groups || [];
  const siteCountries = filterOptions?.site_countries || [];
  const supplierCountries = filterOptions?.supplier_countries || [];

  // Dependent-filter cascades (shared with the customer sectioned panel).
  const {
    companySiteOptions,
    warehouseOptions,
    supplierSiteOptions,
    handleCompanyCountryChange,
    handleDivisionChange,
    handleCompanySiteChange,
    handleSupplierGroupChange,
    handleSupplierCountryChange,
  } = usePOLinesFilterCascades(filters, filterOptions, onFilterChange);

  return (
    <Card>
      <CardHeader className="py-3">
        <div className="flex items-center justify-between">
          <CardTitle>Filter SO Lines</CardTitle>
          {onHideFilters && (
            <Button variant="outline" size="sm" onClick={onHideFilters}>
              Hide Filters
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <form onSubmit={(e) => { e.preventDefault(); onSearch(); }} className="space-y-0">
          {/* CR-6347: Sections with shaded headers, icons and dividers matching Pricebooks layout */}
          <div className="[&>section]:border-t-0">
            <SectionDivider />

            {/* Date Range */}
            <FilterSection title="Date Range" icon={Calendar}>
              <div>
                <label className={LABEL_CLS}>ODD From</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="date"
                    value={filters.oddFrom}
                    onChange={(e) => onFilterChange('oddFrom', e.target.value)}
                    className={`${FIELD_CLS} pl-9`}
                  />
                </div>
              </div>
              <div>
                <label className={LABEL_CLS}>ODD To</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="date"
                    value={filters.oddTo}
                    onChange={(e) => onFilterChange('oddTo', e.target.value)}
                    className={`${FIELD_CLS} pl-9`}
                  />
                </div>
              </div>
            </FilterSection>

            <SectionDivider />

            <CompanySection
              filters={filters}
              onFilterChange={onFilterChange}
              siteCountries={siteCountries}
              divisions={divisions}
              companySiteOptions={companySiteOptions}
              warehouseOptions={warehouseOptions}
              onCompanyCountryChange={handleCompanyCountryChange}
              onDivisionChange={handleDivisionChange}
              onCompanySiteChange={handleCompanySiteChange}
              icon={Building2}
            />

            <SectionDivider />

            <SupplierSection
              filters={filters}
              onFilterChange={onFilterChange}
              supplierCountries={supplierCountries}
              supplierGroups={supplierGroups}
              supplierSiteOptions={supplierSiteOptions}
              onSupplierGroupChange={handleSupplierGroupChange}
              onSupplierCountryChange={handleSupplierCountryChange}
              icon={Truck}
            />

            <SectionDivider />

            <OrderDetailsSection
              filters={filters}
              onFilterChange={onFilterChange}
              isSupplierUser={isSupplierUser}
              icon={FileText}
            />
          </div>

          {/* Footer actions - CR-6347: Clear All left, Apply Filters right */}
          <div className="flex items-center justify-between px-6 py-4">
            <Button type="button" variant="outline" onClick={onClearFilters} className="px-6">
              <X className="h-4 w-4 mr-2" />
              Clear All
            </Button>
            <Button type="submit" className="px-6">
              <Search className="h-4 w-4 mr-2" />
              Apply Filters
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
